import { useMemo } from "react";
import Fuse from "fuse.js";
import type { HelpArticle, HelpLocale } from "./articles";
import { getArticles } from "./articles";

export interface HelpSearchResult {
  article: HelpArticle;
  score?: number;
}

function createFuse(articles: HelpArticle[]) {
  return new Fuse(articles, {
    // Articles render via compiled MDX components, so raw body text is not
    // available to index here — search over the frontmatter fields only.
    keys: [
      { name: "title", weight: 0.6 },
      { name: "description", weight: 0.25 },
      { name: "keywords", weight: 0.15 },
    ],
    // DE-tuned per research: compound words need wider threshold and shorter min chars
    threshold: 0.4,
    minMatchCharLength: 3,
    ignoreLocation: true,
    includeScore: true,
  });
}

export function useHelpSearch(locale: HelpLocale, query: string): HelpSearchResult[] {
  const articles = useMemo(() => getArticles(locale), [locale]);
  const fuse = useMemo(() => createFuse(articles), [articles]);

  return useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      return articles.map((article) => ({ article }));
    }
    return fuse.search(trimmed).map((r) => ({ article: r.item, score: r.score }));
  }, [articles, fuse, query]);
}
